import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { HorseIcon } from '../../icons/sidebarIcons';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

function genderLabel(value, t) {
  // ملاحظة: مفتاح 'stallion' في الترجمة كائن متداخل، لذا نستخدم gender_* (نصوص).
  if (value === 'M') return t('gender_mare');
  if (value === 'S') return t('gender_stallion');
  if (value === 'G') return t('gender_gelding');
  return value || '-';
}

export default function OwnerDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ownerId } = useParams();
  const [owner, setOwner] = useState(null);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const [ownerRes, horsesRes] = await Promise.all([
          axios.get(`${apiUrl}/owners/${ownerId}`, { headers }),
          axios.get(`${apiUrl}/owners/${ownerId}/horses-with-details`, { headers }),
        ]);
        setOwner(ownerRes.data);
        setHorses(Array.isArray(horsesRes.data) ? horsesRes.data : []);
      } catch (err) {
        setError(err?.response?.data?.message || t('error_loading_owner_details') || 'Failed to load owner details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ownerId, t]);

  const stats = useMemo(() => ({
    total: horses.length,
    mares: horses.filter((horse) => horse.gender === 'M').length,
    stallions: horses.filter((horse) => horse.gender === 'S').length,
    withRfid: horses.filter((horse) => horse.rfid_chip_number).length,
  }), [horses]);

  const fullName = owner ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() : '';

  return (
    <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 64, height: 64, bgcolor: COLORS.goldSoft, color: COLORS.gold, fontWeight: 900 }}>
              {fullName?.[0] || 'O'}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {fullName || t('owner_details') || 'Owner details'}
              </Typography>
              <Typography sx={{ color: COLORS.muted }}>{owner?.phone || '-'} · {owner?.email || '-'}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/owner-list')} sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('back') || 'Back'}
            </Button>
            <Button startIcon={<EditIcon />} variant="contained" onClick={() => navigate(`/owner-edit/${ownerId}`)} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>
              {t('edit') || 'Edit'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading && <CircularProgress size={28} sx={{ my: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && owner && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={3}><Metric label={t('total_horses') || 'Total horses'} value={stats.total} /></Grid>
          <Grid item xs={12} md={3}><Metric label={t('mares') || 'Mares'} value={stats.mares} color={COLORS.rose} soft={COLORS.roseSoft} /></Grid>
          <Grid item xs={12} md={3}><Metric label={t('stallions') || 'Stallions'} value={stats.stallions} color={COLORS.blue} soft={COLORS.blueSoft} /></Grid>
          <Grid item xs={12} md={3}><Metric label={t('with_rfid') || 'With RFID'} value={stats.withRfid} color={COLORS.green} soft={COLORS.greenSoft} /></Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1 }}>
                {t('contact_information') || 'Contact information'}
              </Typography>
              <Info label={t('phone') || 'Phone'} value={owner.phone} />
              <Info label={t('email') || 'Email'} value={owner.email} />
              <Info label={t('address') || 'Address'} value={owner.address} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1 }}>
                {t('owner_horses') || 'Owner horses'}
              </Typography>
              <Stack spacing={1}>
                {horses.map((horse) => (
                  <Paper key={horse.horse_id} elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.card, cursor: 'pointer' }} onClick={() => navigate(`/horse-details/${horse.horse_id}`)}>
                    <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar sx={{ bgcolor: COLORS.goldSoft, color: COLORS.gold }}>
                          <HorseIcon size={20} />
                        </Avatar>
                        <Box>
                          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{horse.name_arabic || horse.name || '-'}</Typography>
                          <Typography variant="caption" sx={{ color: COLORS.muted }}>{horse.name || horse.name_arabic || '-'} · RFID {horse.rfid_chip_number || '-'}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.8}>
                        <Chip size="small" label={genderLabel(horse.gender, t)} sx={{ bgcolor: COLORS.cardSoft, fontWeight: 800 }} />
                        <Chip size="small" label={horse.breed_english || horse.breed || '-'} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 800 }} />
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
                {!horses.length && <Typography sx={{ color: COLORS.muted, py: 2 }}>{t('no_data') || 'No data'}</Typography>}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

function Metric({ label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color }}><HorseIcon size={20} /></Avatar>
        <Box>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function Info({ label, value }) {
  return (
    <Box sx={{ py: 1, borderBottom: `1px solid ${COLORS.line}` }}>
      <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 900 }}>{label}</Typography>
      <Typography sx={{ color: COLORS.text, fontWeight: 700 }}>{value || '-'}</Typography>
    </Box>
  );
}
